import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import PublicNav from '../components/PublicNav';
import { HelpCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export default function FAQPage() {
  const faqSections = [
    {
      title: 'Getting Started',
      questions: [
        {
          q: 'Is it free?',
          a: 'Yes. Creating an account and running a league are both free.'
        },
        {
          q: 'Do I need to know how to code or set anything up?',
          a: "Nope. Sign up, create a league, share the invite link, and you're off. The whole setup takes a few minutes."
        },
        {
          q: 'Do I need to be the League Admin to play?',
          a: 'No. One person creates the league and handles admin duties (scoring, bonus questions, settings). Everyone else just joins and competes.'
        },
        {
          q: 'Can I join more than one league?',
          a: 'Yes. You can be in multiple leagues at the same time — one with family, one with coworkers, whatever you want.'
        },
        {
          q: 'Do I need to watch every episode to play?',
          a: "You'll have more fun if you do, but it's not required. Your points accumulate automatically based on what your drafted castaways do. Bonus questions do require you to submit predictions before each episode, so watching helps."
        }
      ]
    },
    {
      title: 'The Draft',
      questions: [
        {
          q: 'How does the snake draft work?',
          a: "Pick order reverses each round. If you pick first in round one, you pick last in round two, and so on. It's the most common and fair format for fantasy drafts."
        },
        {
          q: "What if we can't all meet at the same time for the draft?",
          a: "Use the auto draft (ranked choice) option. Each player ranks the full contestant list before the draft. The system then drafts in snake order using everyone's rankings automatically. No one needs to be online at the same time."
        },
        {
          q: 'Can we set a time limit on picks during a live draft?',
          a: "Yes. The League Admin can enable a pick timer — 30, 60, or 120 seconds per pick. If time runs out, the system auto-picks the next available player from that person's pre-ranked list."
        },
        {
          q: 'Can we draft the same castaway twice?',
          a: "Only if the League Admin enables double drafting. This is an optional setting typically used in larger leagues where there aren't enough contestants to give everyone unique picks."
        },
        {
          q: 'Can I change my picks after the draft?',
          a: 'No. Draft picks are locked once submitted. Plan accordingly.'
        }
      ]
    },
    {
      title: 'Scoring',
      questions: [
        {
          q: 'Are episodes scored automatically?',
          a: 'No. After each episode airs, the League Admin manually logs the events that happened — immunity wins, votes, idol plays, etc. — then finalizes scoring and notifies players. The system handles all the point math; the admin just enters what happened.'
        },
        {
          q: 'Can the admin fix a scoring mistake?',
          a: 'Yes. Admins can correct individual events and trigger a full recalculation. The leaderboard updates automatically.'
        },
        {
          q: 'Can we customize the point values?',
          a: 'Yes. League Admins can adjust point values for any event before or during the season. Past scoring stays consistent with the rules that were in place at the time.'
        },
        {
          q: "What's the most points a single castaway can earn?",
          a: 'A lot. A castaway who makes it to the end, wins Sole Survivor, appears at Final Tribal Council, and was on a winning tribe a few times can easily earn 60+ points on their own. Finale night is where big swings happen.'
        },
        {
          q: "What happens to points if a castaway quits or gets medevac'd?",
          a: "They lose 10 points by default. It's a penalty for leaving the game early, and it reflects the risk of drafting players who aren't fully committed to lasting the whole game."
        }
      ]
    },
    {
      title: 'Bonus Questions',
      questions: [
        {
          q: 'What are bonus questions?',
          a: "Weekly prediction questions posted by the League Admin before each episode. Players answer before the episode airs, and correct answers earn bonus points. It's a way to stay engaged even when your drafted castaways are having a quiet week."
        },
        {
          q: 'Will bonus questions spoil anything?',
          a: 'No. Bonus questions are predictions only — "Will an idol be played tonight?" not "Here\'s who gets voted out." No spoilers.'
        },
        {
          q: 'Do I have to answer bonus questions?',
          a: 'No, but you should. Missing bonus questions means leaving points on the table, which matters a lot in close leagues.'
        }
      ]
    },
    {
      title: 'Leagues & Seasons',
      questions: [
        {
          q: 'Can our league continue next season?',
          a: 'Yes. Leagues support multiple seasons. When a new season starts, the admin sets it up and everyone drafts again. Historical standings are preserved.'
        },
        {
          q: 'Can we play with people in different time zones or countries?',
          a: 'Yes. Multi-country support is built in.'
        },
        {
          q: 'How many people can be in one league?',
          a: "There's no strict maximum, but most leagues work best with 6–14 players. The draft becomes more interesting with more people competing for the same limited pool of castaways."
        },
        {
          q: 'What happens at the end of the season?',
          a: 'The player with the most cumulative points wins the league season. Final standings are locked once the admin finalizes the last episode.'
        }
      ]
    },
    {
      title: 'Technical',
      questions: [
        {
          q: 'How do I sign up?',
          a: 'With your email or Google account. No credit card required.'
        },
        {
          q: 'Is there a mobile app?',
          a: 'The web app works well on mobile browsers. A dedicated mobile app may come later.'
        },
        {
          q: 'How do I contact support?',
          a: 'Email support@survivorfantasy.live.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pb-24 md:pb-0" style={{ backgroundColor: '#F5F0E6' }}>
      <Helmet>
        <title>FAQ | Survivor Fantasy League</title>
        <meta name="description" content="Answers to the most common questions about Survivor Fantasy League — drafts, scoring, leagues, bonus questions, and more." />
        <link rel="canonical" href="https://survivorfantasy.live/faq" />
        <meta property="og:title" content="FAQ | Survivor Fantasy League" />
        <meta property="og:description" content="Answers to the most common questions about Survivor Fantasy League — drafts, scoring, leagues, bonus questions, and more." />
        <meta property="og:url" content="https://survivorfantasy.live/faq" />
      </Helmet>

      <PublicNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#FF7F50' }} />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#4682B4' }} />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Questions? We Have Answers.
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto" style={{ color: '#F5F0E6' }}>
            Everything you've wondered about Survivor Fantasy League, answered.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <article className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-3xl">
          {faqSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-12">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
                <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm" style={{ backgroundColor: '#FF7F50' }}>
                  {sectionIndex + 1}
                </span>
                {section.title}
              </h2>
              <div className="space-y-4">
                {section.questions.map((item, i) => (
                  <Card key={i} className="bg-white border-2" style={{ borderColor: '#4682B4' }}>
                    <CardContent className="p-6">
                      <h3 className="font-bold flex items-start gap-2" style={{ color: '#1E3A4C' }}>
                        <HelpCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#FF7F50' }} />
                        {item.q}
                      </h3>
                      <p className="mt-2 ml-7" style={{ color: '#4682B4' }}>{item.a}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}

          {/* CTA */}
          <div className="text-center py-8 px-6 rounded-2xl mt-12" style={{ backgroundColor: '#1E3A4C' }}>
            <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Still have questions?
            </h3>
            <p className="mb-6" style={{ color: '#F5F0E6' }}>
              Email us at <a href="mailto:support@survivorfantasy.live" className="underline hover:text-[#FF7F50]">support@survivorfantasy.live</a> or just dive in and start playing.
            </p>
            <Link to="/login?tab=register">
              <Button size="lg" className="text-white px-8" style={{ backgroundColor: '#FF7F50' }}>
                Sign Up Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="py-12" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo-new.png" alt="Survivor Fantasy League" className="w-10 h-10" />
              <div>
                <span className="text-white font-bold text-lg">Survivor Fantasy League</span>
                <p className="text-xs" style={{ color: '#4682B4' }}>The Ultimate Survivor Experience</p>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm" style={{ color: '#F5F0E6' }}>
              <Link to="/" className="hover:text-[#FF7F50] transition-colors">Home</Link>
              <Link to="/how-it-works" className="hover:text-[#FF7F50] transition-colors">How It Works</Link>
              <Link to="/rules" className="hover:text-[#FF7F50] transition-colors">Rules</Link>
              <Link to="/faq" className="hover:text-[#FF7F50] transition-colors">FAQ</Link>
              <Link to="/about" className="hover:text-[#FF7F50] transition-colors">About</Link>
              <Link to="/blog" className="hover:text-[#FF7F50] transition-colors">Blog</Link>
              <Link to="/privacy" className="hover:text-[#FF7F50] transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-[#FF7F50] transition-colors">Terms</Link>
            </div>
          </div>
          
          <div className="border-t mt-8 pt-8 text-center text-sm" style={{ borderColor: '#4682B4', color: '#4682B4' }}>
            <p>© {new Date().getFullYear()} Survivor Fantasy League. Not affiliated with CBS or Survivor.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
